import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { sendToRepair, sendToLocation, sendToTrash, getChildAssets, transferWithChildren } from '../lib/api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Wrench, MapPin, Trash2, Loader2 } from 'lucide-react';
import PhotoUpload from './PhotoUpload';
import { getConditionColor } from '../lib/utils';

export default function SendWorkflow({ open, onClose, asset }) {
  const { locations, repairShops, selectedLocation, getCategoryName, getLocationName } = useApp();
  const [tab, setTab] = useState('repair');
  const [loading, setLoading] = useState(false);

  // Form states
  const [repairShopId, setRepairShopId] = useState('');
  const [repairReason, setRepairReason] = useState('');
  const [repairDate, setRepairDate] = useState(new Date().toISOString().split('T')[0]);

  const [destinationId, setDestinationId] = useState('');
  const [transferDate, setTransferDate] = useState(new Date().toISOString().split('T')[0]);

  const [trashReason, setTrashReason] = useState('');
  const [approvedBy, setApprovedBy] = useState('');
  const [trashDate, setTrashDate] = useState(new Date().toISOString().split('T')[0]);
  const [trashPhotos, setTrashPhotos] = useState([]);

  const [transferPhotos, setTransferPhotos] = useState([]);

  // Parent-child transfer states
  const [childAssets, setChildAssets] = useState([]);
  const [selectedChildrenForTransfer, setSelectedChildrenForTransfer] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(false);

  // Load children when asset is a parent
  useEffect(() => {
    const loadChildren = async () => {
      if (asset && asset.relationship_type === 'PARENT') {
        setLoadingChildren(true);
        try {
          const response = await getChildAssets(asset.id);
          const children = response.data || [];
          setChildAssets(children);
          // Default: select all children for transfer
          setSelectedChildrenForTransfer(children.map(c => c.id));
        } catch (error) {
          console.error('Failed to load children:', error);
          setChildAssets([]);
        } finally {
          setLoadingChildren(false);
        }
      } else {
        setChildAssets([]);
        setSelectedChildrenForTransfer([]);
      }
    };
    
    if (open && asset) {
      loadChildren();
    }
  }, [open, asset]);

  const resetForm = () => {
    setRepairShopId('');
    setRepairReason('');
    setRepairDate(new Date().toISOString().split('T')[0]);
    setDestinationId('');
    setTransferDate(new Date().toISOString().split('T')[0]);
    setTrashReason('');
    setApprovedBy('');
    setTrashDate(new Date().toISOString().split('T')[0]);
    setTrashPhotos([]);
    setTransferPhotos([]);
    setSelectedChildrenForTransfer([]);
    setTab('repair');
  };

  const handleSendRepair = async () => {
    if (!repairShopId) {
      toast.error('Please select a repair shop');
      return;
    }
    if (!repairReason.trim()) {
      toast.error('Please enter a reason');
      return;
    }
    
    setLoading(true);
    try {
      await sendToRepair(asset.id, {
        repair_shop_id: repairShopId,
        reason: repairReason,
        event_date: new Date(repairDate).toISOString(),
      });
      toast.success('Asset sent to repair!');
      resetForm();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send to repair');
    } finally {
      setLoading(false);
    }
  };

  const handleSendLocation = async () => {
    if (!destinationId) {
      toast.error('Please select a destination');
      return;
    }
    if (transferPhotos.length === 0) {
      toast.error('Please add at least one photo documenting the asset condition');
      return;
    }
    
    setLoading(true);
    try {
      // If parent asset with children, use transferWithChildren API
      if (asset.relationship_type === 'PARENT' && childAssets.length > 0) {
        const result = await transferWithChildren(asset.id, {
          to_location_id: destinationId,
          child_asset_ids: selectedChildrenForTransfer,
          photos: transferPhotos,
          event_date: new Date(transferDate).toISOString()
        });
        
        const unlinkedCount = result.data.children_unlinked?.length || 0;
        const transferredCount = result.data.children_transferred || 0;
        
        let message = `Parent asset transferred.`;
        if (transferredCount > 0) message += ` ${transferredCount} children transferred.`;
        if (unlinkedCount > 0) message += ` ${unlinkedCount} children unlinked.`;
        
        toast.success(message);
      } else {
        // Regular transfer for non-parent assets
        await sendToLocation(asset.id, {
          to_location_id: destinationId,
          event_date: new Date(transferDate).toISOString(),
          photos: transferPhotos,
        });
        toast.success('Asset transfer initiated!');
      }
      resetForm();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send');
    } finally {
      setLoading(false);
    }
  };

  const handleSendTrash = async () => {
    if (!trashReason.trim()) {
      toast.error('Please enter a reason');
      return;
    }
    if (!approvedBy.trim()) {
      toast.error('Please enter who approved this');
      return;
    }
    if (trashPhotos.length === 0) {
      toast.error('Please add at least one photo documenting the asset condition');
      return;
    }
    
    setLoading(true);
    try {
      await sendToTrash(asset.id, {
        reason: trashReason,
        approved_by_note: approvedBy,
        event_date: new Date(trashDate).toISOString(),
        photos: trashPhotos,
      });
      toast.success('Asset trashed!');
      resetForm();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trash');
    } finally {
      setLoading(false);
    }
  };

  // Toggle child selection for transfer
  const toggleChildForTransfer = (childId) => {
    setSelectedChildrenForTransfer(prev => 
      prev.includes(childId) 
        ? prev.filter(id => id !== childId)
        : [...prev, childId]
    );
  };

  if (!asset) return null;

  const isParentWithChildren = asset.relationship_type === 'PARENT' && childAssets.length > 0;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) { resetForm(); onClose(); } }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Send Asset</DialogTitle>
          <DialogDescription>
            <span className="font-mono font-bold">{asset.barcode}</span>
            <span className="text-gray-500 ml-2">({getCategoryName(asset.category_id)})</span>
            {asset.relationship_type === 'PARENT' && (
              <Badge className="ml-2 bg-purple-100 text-purple-800">Parent</Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="repair" data-testid="send-repair-tab">Repair</TabsTrigger>
            <TabsTrigger value="location" data-testid="send-location-tab">Location</TabsTrigger>
            <TabsTrigger value="trash" data-testid="send-trash-tab">Trash</TabsTrigger>
          </TabsList>

          {/* To Repair */}
          <TabsContent value="repair" className="space-y-4">
            <div>
              <Label>Repair Shop *</Label>
              <Select value={repairShopId} onValueChange={setRepairShopId}>
                <SelectTrigger data-testid="repair-shop-select">
                  <SelectValue placeholder="Select repair shop" />
                </SelectTrigger>
                <SelectContent>
                  {repairShops.filter(s => s.is_active !== false).map((shop) => (
                    <SelectItem key={shop.id} value={shop.id}>{shop.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason *</Label>
              <Textarea
                value={repairReason}
                onChange={(e) => setRepairReason(e.target.value)}
                placeholder="Why does this asset need repair?"
                data-testid="repair-reason-input"
              />
            </div>
            <div>
              <Label>Date Sent</Label>
              <Input
                type="date"
                value={repairDate}
                onChange={(e) => setRepairDate(e.target.value)}
                data-testid="repair-date-input"
              />
            </div>
            <Button 
              onClick={handleSendRepair} 
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600"
              data-testid="send-repair-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wrench className="h-4 w-4 mr-2" />}
              Send to Repair
            </Button>
          </TabsContent>

          {/* To Location */}
          <TabsContent value="location" className="space-y-4">
            <div>
              <Label>Destination Location *</Label>
              <Select value={destinationId} onValueChange={setDestinationId}>
                <SelectTrigger data-testid="destination-select">
                  <SelectValue placeholder="Select destination" />
                </SelectTrigger>
                <SelectContent>
                  {locations
                    .filter(loc => loc.id !== asset.current_location_id && loc.is_active !== false)
                    .map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Parent-Child Transfer Selection */}
            {isParentWithChildren && (
              <div className="border rounded-lg p-3 bg-purple-50">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-purple-800">Select Children to Transfer</Label>
                  <div className="space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectedChildrenForTransfer(childAssets.map(c => c.id))}
                    >
                      Select All
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectedChildrenForTransfer([])}
                    >
                      Deselect All
                    </Button>
                  </div>
                </div>
                {loadingChildren ? (
                  <div className="text-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                  </div>
                ) : (
                  <>
                    <div className="border rounded bg-white divide-y max-h-[150px] overflow-y-auto">
                      {childAssets.map((child) => (
                        <div key={child.id} className="flex items-center p-2 hover:bg-gray-50">
                          <Checkbox
                            id={`child-${child.id}`}
                            checked={selectedChildrenForTransfer.includes(child.id)}
                            onCheckedChange={() => toggleChildForTransfer(child.id)}
                          />
                          <label htmlFor={`child-${child.id}`} className="ml-2 flex-1 cursor-pointer text-sm">
                            <span className="font-mono font-medium">{child.barcode}</span>
                            <span className="text-gray-500 ml-1">({getCategoryName(child.category_id)})</span>
                          </label>
                          <Badge className={`text-xs ${getConditionColor(child.condition)}`}>{child.condition}</Badge>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-purple-700 mt-2">
                      {selectedChildrenForTransfer.length} of {childAssets.length} children will transfer.
                      {childAssets.length - selectedChildrenForTransfer.length > 0 && (
                        <span className="text-amber-600 ml-1">
                          {childAssets.length - selectedChildrenForTransfer.length} will be unlinked.
                        </span>
                      )}
                    </p>
                  </>
                )}
              </div>
            )}
            
            <div>
              <Label>Date Sent</Label>
              <Input
                type="date"
                value={transferDate}
                onChange={(e) => setTransferDate(e.target.value)}
                data-testid="transfer-date-input"
              />
            </div>
            <PhotoUpload
              photos={transferPhotos}
              onPhotosChange={setTransferPhotos}
              required={true}
              entityType="transfer"
              entityId={asset?.id || 'temp'}
              label="Transfer Photos"
              helpText="Document the asset condition before sending"
            />
            <Button 
              onClick={handleSendLocation} 
              disabled={loading || transferPhotos.length === 0}
              className={`w-full ${transferPhotos.length === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'}`}
              data-testid="send-location-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <MapPin className="h-4 w-4 mr-2" />}
              {isParentWithChildren ? 'Transfer with Children' : 'Send to Location'}
            </Button>
          </TabsContent>

          {/* To Trash */}
          <TabsContent value="trash" className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
              Warning: This action will mark the asset as trashed. Photos are required to document its condition.
            </div>
            <div>
              <Label>Reason *</Label>
              <Textarea
                value={trashReason}
                onChange={(e) => setTrashReason(e.target.value)}
                placeholder="Why is this asset being trashed?"
                data-testid="trash-reason-input"
              />
            </div>
            <div>
              <Label>Approved By *</Label>
              <Input
                value={approvedBy}
                onChange={(e) => setApprovedBy(e.target.value)}
                placeholder="Manager name who approved"
                data-testid="approved-by-input"
              />
            </div>
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={trashDate}
                onChange={(e) => setTrashDate(e.target.value)}
                data-testid="trash-date-input"
              />
            </div>
            <PhotoUpload
              photos={trashPhotos}
              onPhotosChange={setTrashPhotos}
              required={true}
              entityType="trash"
              entityId={asset?.id || 'temp'}
              label="Trash Photos"
              helpText="Document the asset condition at time of disposal"
            />
            <Button 
              onClick={handleSendTrash} 
              disabled={loading || trashPhotos.length === 0}
              className={`w-full ${trashPhotos.length === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'}`}
              data-testid="send-trash-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Trash Asset
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
